import React, { useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  addEdge,
  Controls,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import StartNode from './KnotsEditor/StartNode';
import { StateContext } from './StateContainer';
import ModalSaveConversation from './KnotsEditor/modal/ModalSaveConversation';
import ModalLiveVersion from './KnotsEditor/modal/ModalLiveVersion';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ModalJsonEditor from './KnotsEditor/modal/ModalJsonEditor';
import ActionButton from './KnotsEditor/edgeType/ActionButton';
import ModalDeleteNode from './KnotsEditor/modal/ModalDeleteNode';
import ModalDeleteVersion from './KnotsEditor/modal/ModalDeleteVersion';

import KnotsEditorHeader from './KnotsEditor/header/KnotsEditorHeader';
import NodeModal from './KnotsEditor/editor-modals/NodeModal';
import IntentModal from './KnotsEditor/editor-modals/IntentModal';
import ModalAiJsonGenerator from './KnotsEditor/modal/ModalAiJsonGenerator';
import SelfConnectingEdge from './KnotsEditor/edgeType/SelfConnectingEdge';

const nodeTypes = {
  start: StartNode,
  end: StartNode,
  execution: StartNode,
  relay: StartNode,
};

const edgeTypes = {
  custom_edge: ActionButton,
  selfconnecting: SelfConnectingEdge,
};

const snapGrid = [20, 20];

const KnotsEditor = ({ editorValueString }) => {
  const { id } = useParams();

  const {
    versioniAgenteConversazionale,
    setVersioniAgenteConversazionale,
    selectedNode,
    setSelectedNode,
    token,
    setError,
    dettagliNodi,
    setDettagliNodi,

    // Nodes
    nodes,
    setNodes,
    onNodesChange,

    // Edges
    edges,
    setEdges,
    onEdgesChange,

    // Current conversational version
    currentConversationalVersion,
    setCurrentConversationalVersion,

    // Viewport
    viewport,
    setViewport,

    // Selected Edge
    selectedEdge,
    setSelectedEdge,
  } = useContext(StateContext);

  const onViewportChange = useCallback((viewport) => {
    setViewport(viewport);
  }, []);

  useEffect(() => {
    axios({
      method: 'post',
      url: `${process.env.REACT_APP_BASE_URL}/project/detail`,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        projectCode: id,
      },
    })
      .then((response) => {
        let editorValueArray = JSON.parse(
          response.data.response.conversationalJSON
        );

        if (editorValueArray.viewport) {
          setViewport(editorValueArray.viewport);
        }

        if (editorValueArray) {
          const newNodes = Object.keys(editorValueArray)
            .filter(
              (key) => key !== 'fullfillment_variables' && key !== 'viewport'
            )
            .map((key) => {
              const nodeData = editorValueArray[key];
              return {
                id: nodeData.id ? nodeData.id.toString() : '',
                type: nodeData.type,
                data: { label: nodeData.node_label, actions: [] },
                position: nodeData.position,
              };
            });

          setNodes(newNodes);

          const listFullfillmentVariables =
            editorValueArray.fullfillment_variables || [];

          const fullfillmentVariables = listFullfillmentVariables.filter(
            (fv) =>
              fv.label === '' &&
              fv.nome_variabile === '' &&
              fv.istruzioni_estrazione === ''
          );

          const newDettagliNodi = Object.keys(editorValueArray).reduce(
            (acc, key) => {
              if (key !== 'fullfillment_variables' && key !== 'viewport') {
                const nodeData = editorValueArray[key];
                acc[key] = {
                  node_label: nodeData.node_label,
                  id: nodeData.id,
                  type: 'start',
                  position: nodeData.position,
                  intents: nodeData.intents || [],
                  fullfillment:
                    fullfillmentVariables.filter(
                      (fv) => fv.nodeId === nodeData.id
                    ) || [],
                };
              }
              return acc;
            },
            {}
          );

          setDettagliNodi(
            JSON.parse(response.data.response.conversationalJSON)
          );

          setVersioniAgenteConversazionale(
            response.data.response.versionsConversational
          );

          const liveVersion =
            response.data.response.versionsConversational.find(
              (version) => version.isLive
            );
          if (liveVersion) {
            setCurrentConversationalVersion(liveVersion.id);
          }

          const newEdges = [];
          Object.keys(newDettagliNodi).forEach((key) => {
            const node = newDettagliNodi[key];
            if (node.intents) {
              node.intents.forEach((intent) => {
                let source = node.id.toString();
                let target = intent.target.toString();
                newEdges.push({
                  id: `${node.id}-${intent.target}`,
                  source: source,
                  target: target,
                  animated: true,
                  type: source === target ? 'selfconnecting' : 'custom_edge',
                  markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                  },
                  data: {
                    trigger: intent.trigger,
                    output_intent: intent.output_intent,
                    forceIntent: intent.forceIntent,
                    type: 'action',
                    id: intent.id,
                    target: intent.target,
                    source: node.id,
                  },
                });
              });
            }
          });

          setEdges(newEdges);
        } else {
          const initialNode = {
            id: '1',
            type: 'start',
            data: { label: 'Nodo 1', actions: [] },
            position: { x: 0, y: 50 },
          };

          setNodes([initialNode]);
          setDettagliNodi({
            'nodo-1': {
              node_label: 'Nodo 1',
              id: 1,
              position: { x: 0, y: 50 },
            },
          });
        }
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setError(err);
      });
  }, [id, token, setError]);

  const onConnect = useCallback(
    (params) => {
      const sourceNodeId = params.source;
      const targetNodeId = params.target;

      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: true,
            type:
              sourceNodeId.toString() === targetNodeId.toString()
                ? 'selfconnecting'
                : 'custom_edge',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
            },
            id: `${sourceNodeId}-${targetNodeId}`,
            source: sourceNodeId.toString(),
            target: targetNodeId.toString(),
          },
          eds
        )
      );

      setDettagliNodi((prev) => {
        const updatedDetails = { ...prev };

        if (!updatedDetails[`nodo-${sourceNodeId}`].intents) {
          updatedDetails[`nodo-${sourceNodeId}`].intents = [];
        }

        const intentExists = updatedDetails[
          `nodo-${sourceNodeId}`
        ].intents.some((intent) => intent.target === targetNodeId);

        if (!intentExists) {
          updatedDetails[`nodo-${sourceNodeId}`].intents.push({
            trigger: '',
            output_intent: '',
            target: targetNodeId,
            forceIntent: false,
            id: `${sourceNodeId}-${targetNodeId}`,
            source: sourceNodeId,
            target: targetNodeId,
          });
        }

        return updatedDetails;
      });
    },
    [setEdges, setDettagliNodi]
  );

  const onEdgeClick = (event, edge) => {
    const sourceNodeId = edge.source;
    const existingIntent = dettagliNodi[`nodo-${sourceNodeId}`]?.intents?.find(
      (intent) => intent.target === edge.target
    );

    setSelectedEdge({
      ...edge,
      trigger: existingIntent?.trigger || '',
      output_intent: existingIntent?.output_intent || '',
      forceIntent: existingIntent?.forceIntent || '',
    });

    setSelectedNode(null);
  };

  const onEdgesDelete = (deleted) => {
    setEdges((eds) => eds.filter((e) => !deleted.some((d) => d.id === e.id)));
    setDettagliNodi((prev) => {
      const updatedDetails = { ...prev };
      deleted.forEach((edge) => {
        if (updatedDetails[`nodo-${edge.source}`]?.intents) {
          updatedDetails[`nodo-${edge.source}`].intents = updatedDetails[
            `nodo-${edge.source}`
          ].intents.filter((intent) => intent.target !== edge.target);
        }
      });
      return updatedDetails;
    });
  };

  const onNodesDelete = (deletedItems) => {
    const cancelNodeButton = document.getElementById('ModalCancelNodeButton');
    const deleted = Array.isArray(deletedItems) ? deletedItems : [deletedItems];

    // Controlla se tra i nodi da eliminare c'è uno con classe 'start'
    const hasStartNode = deleted.some((node) => node.type === 'start');
    if (hasStartNode) {
      toast.error('Non puoi eliminare il nodo di inizio', { type: 'error' });
      return;
    }

    const deletedIds = deleted.map((node) => node.id.toString());

    // Rimuove i nodi eliminati
    setNodes((nds) => nds.filter((node) => !deletedIds.includes(node.id)));

    // Rimuove gli edge che hanno source o target tra i nodi eliminati
    setEdges((eds) =>
      eds.filter(
        (edge) =>
          !deletedIds.includes(edge.source) && !deletedIds.includes(edge.target)
      )
    );
    // Rimuovi i dettagli dei nodi e gli intents associati
    setDettagliNodi((prev) => {
      const updatedDetails = { ...prev };

      deletedIds.forEach((id) => {
        delete updatedDetails[`nodo-${id}`];

        Object.keys(updatedDetails).forEach((key) => {
          if (updatedDetails[key]?.intents) {
            updatedDetails[key].intents = updatedDetails[key].intents.filter(
              (intent) => intent.source !== id && intent.target !== id
            );
          }
        });
      });

      return updatedDetails;
    });

    cancelNodeButton.click();
  };

  const selectedNodeId = selectedNode ? parseInt(selectedNode.id) : '';
  const nodoKey = selectedNodeId ? `nodo-${selectedNodeId}` : '';

  return (
    <div
      className="position-relative"
      style={{
        height: '80vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <KnotsEditorHeader />
      <ReactFlow
        // onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        nodes={nodes}
        edges={edges}
        deleteKeyCode={null}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        // style={{ background: '#F5F5F5', flexGrow: 1 }}
        snapToGrid={true}
        snapGrid={snapGrid}
        viewport={viewport}
        onViewportChange={onViewportChange}
        fitView
      >
        {/* Aggiunge lo sfondo a puntini */}
        <Background variant="dots" gap={12} size={2} color="#ccc" />
        <Controls />
      </ReactFlow>

      {selectedNode && <NodeModal />}

      {selectedEdge && <IntentModal />}
      <ToastContainer />
      <ModalSaveConversation
        versioniAgenteConversazionale={versioniAgenteConversazionale}
        edito
        setDettagliNodi={setDettagliNodi}
        currentConversationalVersion={currentConversationalVersion}
        setCurrentConversationalVersion={setCurrentConversationalVersion}
        setVersioniAgenteConversazionale={setVersioniAgenteConversazionale}
        dettagliNodi={dettagliNodi}
        id={id}
        nodes={nodes}
        setNodes={setNodes}
        viewport={viewport}
        edges={edges}
        setEdges={setEdges}
      />
      <ModalDeleteVersion
        versioniAgenteConversazionale={versioniAgenteConversazionale}
        edito
        setDettagliNodi={setDettagliNodi}
        currentConversationalVersion={currentConversationalVersion}
        setCurrentConversationalVersion={setCurrentConversationalVersion}
        setVersioniAgenteConversazionale={setVersioniAgenteConversazionale}
        dettagliNodi={dettagliNodi}
        id={id}
        nodes={nodes}
        setNodes={setNodes}
        viewport={viewport}
        edges={edges}
        setEdges={setEdges}
      />
      <ModalLiveVersion
        versioniAgenteConversazionale={versioniAgenteConversazionale}
        setDettagliNodi={setDettagliNodi}
        currentConversationalVersion={currentConversationalVersion}
        setCurrentConversationalVersion={setCurrentConversationalVersion}
        dettagliNodi={dettagliNodi}
        setVersioniAgenteConversazionale={setVersioniAgenteConversazionale}
        id={id}
        nodes={nodes}
      />
      <ModalJsonEditor
        versioniAgenteConversazionale={versioniAgenteConversazionale}
        setDettagliNodi={setDettagliNodi}
        currentConversationalVersion={currentConversationalVersion}
        setCurrentConversationalVersion={setCurrentConversationalVersion}
        dettagliNodi={dettagliNodi}
        setVersioniAgenteConversazionale={setVersioniAgenteConversazionale}
        id={id}
        nodes={nodes}
        setNodes={setNodes}
        edges={edges}
        setEdges={setEdges}
      />
      <ModalAiJsonGenerator
        versioniAgenteConversazionale={versioniAgenteConversazionale}
        setDettagliNodi={setDettagliNodi}
        currentConversationalVersion={currentConversationalVersion}
        setCurrentConversationalVersion={setCurrentConversationalVersion}
        dettagliNodi={dettagliNodi}
        setVersioniAgenteConversazionale={setVersioniAgenteConversazionale}
        id={id}
        nodes={nodes}
        setNodes={setNodes}
        edges={edges}
        setEdges={setEdges}
      />
      <ModalDeleteNode onNodesDelete={onNodesDelete} />
    </div>
  );
};

export default KnotsEditor;
